import React, { useState, useRef, useEffect } from 'react';
import './TableLayout.css';

const TableLayout = ({ tables, onUpdatePosition, onUpdateStatus, onDelete }) => {
  const [selectedTable, setSelectedTable] = useState(null);
  const [draggingTable, setDraggingTable] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showContextMenu, setShowContextMenu] = useState(null);
  const [zoom, setZoom] = useState(1);
  const layoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowContextMenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleMouseDown = (table, e) => {
    if (e.button === 2) return; // Right click
    
    const rect = e.currentTarget.getBoundingClientRect();
    const layoutRect = layoutRef.current.getBoundingClientRect();
    
    setDraggingTable(table.id);
    setDragOffset({
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom
    });
    setSelectedTable(table);
  };

  const handleMouseMove = (e) => {
    if (!draggingTable) return;
    
    const layoutRect = layoutRef.current.getBoundingClientRect();
    const newX = (e.clientX - layoutRect.left) / zoom - dragOffset.x;
    const newY = (e.clientY - layoutRect.top) / zoom - dragOffset.y;
    
    const table = tables.find(t => t.id === draggingTable);
    if (table) {
      onUpdatePosition(draggingTable, Math.max(0, newX), Math.max(0, newY));
    }
  };

  const handleMouseUp = () => {
    setDraggingTable(null);
  };

  const handleRightClick = (table, e) => {
    e.preventDefault();
    setShowContextMenu({
      table,
      x: e.clientX,
      y: e.clientY
    });
    setSelectedTable(table);
  };

  const getTableSize = (capacity) => {
    if (capacity <= 2) return { width: 60, height: 60 };
    if (capacity <= 4) return { width: 80, height: 80 };
    if (capacity <= 6) return { width: 100, height: 100 };
    return { width: 120, height: 120 };
  };

  const getTableColor = (status) => {
    switch (status) {
      case 'available': return '#2ecc71';
      case 'occupied': return '#e74c3c';
      case 'reserved': return '#f39c12';
      case 'cleaning': return '#9b59b6';
      default: return '#95a5a6';
    }
  };

  const renderTable = (table) => {
    const size = getTableSize(table.capacity);
    const color = getTableColor(table.status);
    const isDragging = draggingTable === table.id;
    const isSelected = selectedTable?.id === table.id;

    return (
      <div
        key={table.id}
        className={`draggable-table ${isDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''} shape-${table.shape || 'square'}`}
        style={{
          left: `${(table.positionX || 100) * zoom}px`,
          top: `${(table.positionY || 100) * zoom}px`,
          width: `${size.width * zoom}px`,
          height: `${size.height * zoom}px`,
          backgroundColor: color,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={(e) => handleMouseDown(table, e)}
        onContextMenu={(e) => handleRightClick(table, e)}
      >
        <div className="table-label">
          <strong>{table.tableNumber}</strong>
          <span>{table.capacity}p</span>
        </div>
        <div className="table-status-badge">
          {table.status === 'available' && '✓'}
          {table.status === 'occupied' && '●'}
          {table.status === 'reserved' && '📅'}
          {table.status === 'cleaning' && '🧹'}
        </div>
      </div>
    );
  };

  return (
    <div className="table-layout-container">
      <div className="layout-toolbar">
        <div className="toolbar-section">
          <button 
            className="tool-btn"
            onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
          >
            🔍−
          </button>
          <span className="zoom-level">{Math.round(zoom * 100)}%</span>
          <button 
            className="tool-btn"
            onClick={() => setZoom(Math.min(2, zoom + 0.1))}
          >
            🔍+
          </button>
          <button 
            className="tool-btn"
            onClick={() => setZoom(1)}
          >
            Reset Zoom
          </button>
        </div>
        
        <div className="toolbar-section">
          <div className="legend">
            <span className="legend-item">
              <span className="legend-color" style={{background: '#2ecc71'}}></span>
              Available
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background: '#e74c3c'}}></span>
              Occupied
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background: '#f39c12'}}></span>
              Reserved
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background: '#9b59b6'}}></span>
              Cleaning
            </span>
          </div>
        </div>

        <div className="toolbar-section">
          <p className="help-text">💡 Drag tables to reposition | Right-click for options</p>
        </div>
      </div>

      <div 
        ref={layoutRef}
        className="layout-canvas"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`
        }}
      >
        {tables.map(renderTable)}
      </div>

      {showContextMenu && (
        <div 
          className="context-menu"
          style={{
            left: `${showContextMenu.x}px`,
            top: `${showContextMenu.y}px`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header">
            Table {showContextMenu.table.tableNumber}
          </div>
          <button 
            className="context-menu-item"
            onClick={() => {
              onUpdateStatus(showContextMenu.table.id, 'available');
              setShowContextMenu(null);
            }}
          >
            ✅ Mark Available
          </button>
          <button 
            className="context-menu-item"
            onClick={() => {
              onUpdateStatus(showContextMenu.table.id, 'occupied');
              setShowContextMenu(null);
            }}
          >
            🔴 Mark Occupied
          </button>
          <button 
            className="context-menu-item"
            onClick={() => {
              onUpdateStatus(showContextMenu.table.id, 'reserved');
              setShowContextMenu(null);
            }}
          >
            📅 Mark Reserved
          </button>
          <button 
            className="context-menu-item"
            onClick={() => {
              onUpdateStatus(showContextMenu.table.id, 'cleaning');
              setShowContextMenu(null);
            }}
          >
            🧹 Mark Cleaning
          </button>
          <div className="context-menu-divider"></div>
          <button 
            className="context-menu-item danger"
            onClick={() => {
              onDelete(showContextMenu.table.id);
              setShowContextMenu(null);
            }}
          >
            🗑️ Delete Table
          </button>
        </div>
      )}

      {selectedTable && (
        <div className="table-info-panel">
          <h4>Table {selectedTable.tableNumber}</h4>
          <p><strong>Capacity:</strong> {selectedTable.capacity} people</p>
          <p><strong>Floor:</strong> {selectedTable.floor}</p>
          <p><strong>Section:</strong> {selectedTable.section}</p>
          <p><strong>Status:</strong> <span className={`status-badge status-${selectedTable.status}`}>{selectedTable.status}</span></p>
          <p><strong>Position:</strong> X: {Math.round(selectedTable.positionX)}, Y: {Math.round(selectedTable.positionY)}</p>
        </div>
      )}
    </div>
  );
};

export default TableLayout;
